import { Button, Card, Input, Option, Select, Stack, ToggleButtonGroup, Typography } from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetScreeningCaseByIdQueryKey,
  ScreeningCaseDto,
  ScreeningClientType,
  ScreeningDesiredTest,
  ScreeningStatus,
  useGetScreeningCaseByIdSuspense,
  useUpdateScreeningCase,
} from '../../../../api/created/screening.ts';
import { AddressContactDto, AddressDto, AddressDtoAddressType } from '../../../../api/generated/model';
import { GermanDateInput } from '../../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../../components/form/InstitutionContactFields.tsx';
import { SingeLineFormGrid, SingleLineFormControl } from '../../../../components/form/SingeLineFormComponents.tsx';
import { formatDate } from '../../../../utils/date.ts';

export interface ScreeningFormValues {
  status: ScreeningStatus;
  clientType: ScreeningClientType | null;
  institution: AddressDto | null;
  contactPerson: AddressContactDto | null;
  groupName: string;
  desiredTest: ScreeningDesiredTest | null;
  testDate: string;
}

const clientTypeOptions = [
  { value: ScreeningClientType.BAZ_ZURICH, label: 'BAZ Zürich' },
  { value: ScreeningClientType.SELF_REFERRAL, label: 'Selbstmelder' },
  { value: ScreeningClientType.OTHER, label: 'Andere' },
];

const desiredTestOptions = [
  { value: ScreeningDesiredTest.MX, label: 'Mx' },
  { value: ScreeningDesiredTest.QFT, label: 'QFT' },
  { value: ScreeningDesiredTest.RX, label: 'Rx' },
];

const statusOptions = [
  { value: ScreeningStatus.OPEN, label: 'Offen' },
  { value: ScreeningStatus.CLOSED, label: 'Abgeschlossen' },
];

export function getScreeningFormDefaults(screening: ScreeningCaseDto): ScreeningFormValues {
  return {
    status: screening.status,
    clientType: screening.clientType,
    institution: screening.institution,
    contactPerson: screening.contactPerson,
    groupName: screening.groupName,
    desiredTest: screening.desiredTest,
    testDate: getDateInputValue(screening.testDate),
  };
}

export function EditScreeningForm({ screeningId }: { screeningId: string }) {
  const queryClient = useQueryClient();
  const { data: screening } = useGetScreeningCaseByIdSuspense(screeningId);

  const updateScreeningMutation = useUpdateScreeningCase({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetScreeningCaseByIdQueryKey(screeningId), data);
        reset(getScreeningFormDefaults(data));
      },
      onError: () => toast.error('Beim Speichern des Screening-Falls ist ein Fehler aufgetreten'),
    },
  });

  const {
    register,
    control,
    getFieldState,
    reset,
    watch,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ScreeningFormValues>({
    defaultValues: getScreeningFormDefaults(screening),
    mode: 'onBlur',
  });
  const clientType = watch('clientType');
  const status = watch('status');
  const isOtherClientType = clientType === ScreeningClientType.OTHER;
  const isSavingScreening = updateScreeningMutation.isPending;
  // The backend gates whether the screening case may be marked as closed (e.g. all individuals processed).
  const canCloseScreening = screening.canClose !== false;

  useEffect(() => {
    reset(getScreeningFormDefaults(screening));
  }, [reset, screening]);

  useEffect(() => {
    if (!isOtherClientType) {
      setValue('institution', null);
      setValue('contactPerson', null);
      clearErrors(['institution', 'contactPerson']);
    }
  }, [clearErrors, isOtherClientType, setValue]);

  const scheduleSave = useCallback(() => {
    void handleSubmit((data) => {
      if (!data.clientType) return;
      const isOtherClientTypeValue = data.clientType === ScreeningClientType.OTHER;

      if (isOtherClientTypeValue && !canSaveOtherClientType(data)) {
        if (!data.institution) {
          setError('institution', {
            type: 'required',
            message: 'Institution muss ausgewählt werden',
          });
          clearErrors('contactPerson');
        } else {
          clearErrors('institution');

          if (!data.contactPerson) {
            setError('contactPerson', {
              type: 'required',
              message: 'Ansprechperson muss ausgewählt werden',
            });
          } else {
            clearErrors('contactPerson');
          }
        }

        return;
      }

      clearErrors(['institution', 'contactPerson']);

      updateScreeningMutation.mutate({
        id: screeningId,
        data: {
          screeningAssignment: null,
          clientType: data.clientType,
          institution: isOtherClientTypeValue ? data.institution : null,
          contactPerson: isOtherClientTypeValue ? data.contactPerson : null,
          groupName: data.groupName,
          desiredTest: data.desiredTest,
          testDate: data.testDate || null,
          status: data.status,
          assignedEmployeeId: screening.assignedEmployee?.id ?? null,
        },
      });
    })();
  }, [clearErrors, handleSubmit, screening.assignedEmployee?.id, screeningId, setError, updateScreeningMutation]);

  const scheduleSaveAfterFieldUpdate = useCallback(() => {
    window.setTimeout(scheduleSave);
  }, [scheduleSave]);

  function registerWithSaveOnBlur(fieldName: 'groupName') {
    const fieldRegistration = register(fieldName);

    return {
      ...fieldRegistration,
      onBlur: (event: Parameters<typeof fieldRegistration.onBlur>[0]) => {
        void fieldRegistration.onBlur(event);
        scheduleSave();
      },
    };
  }

  return (
    <Stack direction="row" gap={2}>
      <Stack flex={1} gap={2}>
        <Card>
          <SingeLineFormGrid>
            <Typography level="h4">Screeninginformationen</Typography>

            <SingleLineFormControl label="Auftraggeber" fieldState={getFieldState('clientType')}>
              <Controller
                name="clientType"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    onChange={(_, value) => {
                      onChange(value);
                      scheduleSaveAfterFieldUpdate();
                    }}
                    variant="plain">
                    {clientTypeOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </SingleLineFormControl>

            <InstitutionContactFields
              formMethods={{ control, watch, setValue, getFieldState }}
              layout="singleLine"
              addressField="institution"
              contactField="contactPerson"
              addressLabel="Institution"
              contactLabel="Ansprechperson"
              addressTypes={Object.values(AddressDtoAddressType)}
              canChangeAddressType={true}
              isAddressDisabled={!isOtherClientType}
              isContactDisabled={!isOtherClientType}
              variant="plain"
              clearContactOnAddressChange="always"
              contactClearOptions={false}
              useAllContactsList={true}
              addressHelperText={errors.institution?.message}
              contactHelperText={errors.contactPerson?.message}
              onAddressChange={(address) => {
                if (address) {
                  clearErrors('institution');
                }
                scheduleSaveAfterFieldUpdate();
              }}
              onContactChange={(contact) => {
                if (contact) {
                  clearErrors('contactPerson');
                }
                scheduleSaveAfterFieldUpdate();
              }}
            />

            <SingleLineFormControl label="Gruppenbezeichnung" fieldState={getFieldState('groupName')}>
              <Input {...registerWithSaveOnBlur('groupName')} variant="plain" />
            </SingleLineFormControl>

            <SingleLineFormControl label="Gewünschter Test" fieldState={getFieldState('desiredTest')}>
              <Controller
                name="desiredTest"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    onChange={(_, value) => {
                      onChange(value);
                      scheduleSaveAfterFieldUpdate();
                    }}
                    variant="plain">
                    {desiredTestOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </SingleLineFormControl>

            <SingleLineFormControl label="Testtermin" fieldState={getFieldState('testDate')}>
              <GermanDateInput control={control} name="testDate" variant="plain" onValueBlur={() => scheduleSave()} />
            </SingleLineFormControl>
          </SingeLineFormGrid>
        </Card>
      </Stack>

      <Stack flex={1} gap={2}>
        <Card>
          <SingeLineFormGrid>
            <Typography level="h4">Status</Typography>

            <SingleLineFormControl label="Status" fieldState={getFieldState('status')}>
              <Controller
                name="status"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ToggleButtonGroup value={value} spacing={1}>
                    {statusOptions.map((option) => {
                      const isClosedDisabled = option.value === ScreeningStatus.CLOSED && !canCloseScreening;
                      const isDisabled = isSavingScreening || isClosedDisabled;

                      return (
                        <Button
                          key={option.value}
                          value={option.value}
                          disabled={isDisabled}
                          onClick={() => {
                            if (isDisabled) return;
                            onChange(option.value);
                            scheduleSaveAfterFieldUpdate();
                          }}>
                          {option.label}
                        </Button>
                      );
                    })}
                  </ToggleButtonGroup>
                )}
              />
            </SingleLineFormControl>

            {status === ScreeningStatus.CLOSED && screening.closedAt && (
              <SingleLineFormControl label="Abgeschlossen am" fieldState={getFieldState('status')}>
                <Typography sx={{ py: 0.75, lineHeight: '23px' }}>{formatDate(screening.closedAt)}</Typography>
              </SingleLineFormControl>
            )}
          </SingeLineFormGrid>
        </Card>
      </Stack>
    </Stack>
  );
}

function getDateInputValue(date?: string | null) {
  return date?.slice(0, 10) ?? '';
}

function canSaveOtherClientType(data: ScreeningFormValues) {
  return !!data.institution && !!data.contactPerson;
}
